/*
 * Opus product page enhancements.
 * - <opus-picker>: one swatch grid across a colour range / split product family
 * - <opus-sticky-buy>: sticky add-to-cart bar
 * - Details section: variant-level specs, description "read more"
 * - Learn section: related Resource Library articles (lazy)
 * Everything defers to Dawn's own <product-info> for variant changes and product swaps.
 */
(() => {
  // Dawn's product swap re-injects section scripts; run once per page
  if (window.__opusPdpLoaded) return;
  window.__opusPdpLoaded = true;

  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  // Dot colours for common colour-group names. Unknown groups get a text pill.
  const GROUP_HUES = {
    black: '#1d1d1b',
    grey: '#8d8d8a',
    gray: '#8d8d8a',
    blue: '#2357c6',
    brown: '#7a4a28',
    green: '#2e8a4b',
    orange: '#f07c1b',
    red: '#cf2b2b',
    pink: '#ee6aa7',
    purple: '#6e3fa6',
    violet: '#6e3fa6',
    white: '#ffffff',
    yellow: '#f6c500',
    other: 'conic-gradient(from 40deg, #d8c690, #f7f1dc, #a99b6c, #e6dcb8, #d8c690)',
    metallic: 'conic-gradient(from 40deg, #d8c690, #f7f1dc, #a99b6c, #e6dcb8, #d8c690)',
  };
  const groupHue = (g) => {
    const k = norm(g).replace(/ colou?rs?$/, '').replace(/s$/, '');
    return GROUP_HUES[k] || null;
  };

  const state = (window.__opusPdpState = window.__opusPdpState || {});

  const cartDrawer = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  async function addItemsToCart(items) {
    const cart = cartDrawer();
    const body = { items };
    if (cart && cart.getSectionsToRender) {
      body.sections = cart.getSectionsToRender().map((s) => s.section || s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'opus-palette', cartData: data });
    }
    if (cart && cart.tagName === 'CART-DRAWER' && cart.renderContents && data.sections) {
      cart.classList.remove('is-empty');
      cart.renderContents(data);
    } else {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }
    return data;
  }

  /* ------------------------------------------------------------------ picker */

  // Spectrum order used for colour groups and for sorting inside them
  const GROUP_ORDER = ['white', 'yellow', 'orange', 'red', 'pink', 'purple', 'violet', 'blue', 'green', 'brown', 'grey', 'gray', 'black', 'other', 'metallic'];
  const groupRank = (g) => {
    const k = norm(g).replace(/ colou?rs?$/, '').replace(/s$/, '');
    const i = GROUP_ORDER.indexOf(k);
    return i === -1 ? GROUP_ORDER.length : i;
  };
  const hexToHsl = (hex) => {
    const n = parseInt(String(hex).replace('#', ''), 16);
    if (Number.isNaN(n)) return null;
    const r = ((n >> 16) & 255) / 255;
    const g = ((n >> 8) & 255) / 255;
    const b = (n & 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    let h = 0;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    if (d) {
      if (max === r) h = ((g - b) / d) % 6;
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
      if (h < 0) h += 360;
    }
    return { h, s, l };
  };
  // Where a measured colour falls when the listing has no colour groups
  const pseudoGroup = (hsl) => {
    if (!hsl) return GROUP_ORDER.length;
    const { h, s, l } = hsl;
    if (l > 0.9 || (s < 0.12 && l > 0.75)) return 0; // white
    if (s < 0.14) return l < 0.2 ? 12 : 10; // black / grey
    if (h >= 12 && h <= 50 && l < 0.42) return 9; // earth / brown
    if (h < 15 || h >= 345) return 3; // red
    if (h < 40) return 2; // orange
    if (h < 70) return 1; // yellow
    if (h < 165) return 8; // green
    if (h < 255) return 7; // blue
    if (h < 300) return 5; // purple
    return 4; // pink / magenta
  };
  // Within a group: walk the wheel yellow → orange → red → violet → blue → green, then light → dark
  const hueKey = (hsl) => (hsl ? (((70 - hsl.h) % 360) + 360) % 360 : 999);
  const NEUTRAL = new Set([0, 10, 11, 12]);

  // Natural size order: 2oz < 5oz < 16oz < 32oz < 128oz, 22ml < 60ml < 1L
  const UNIT = { ml: 1, l: 1000, litre: 1000, liter: 1000, oz: 29.57, floz: 29.57, g: 1, kg: 1000, lb: 453.6, gal: 3785 };
  const sizeVal = (s) => {
    const m = String(s).toLowerCase().replace(/\s+/g, '').match(/([\d.]+)(ml|l|litre|liter|floz|oz|g|kg|lb|gal)\b/);
    return m ? parseFloat(m[1]) * (UNIT[m[2]] || 1) : Infinity;
  };

  class OpusPicker extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      let data;
      try {
        data = JSON.parse(this.querySelector('[data-opc-data]').textContent);
      } catch (e) {
        console.error('opus-picker: bad data', e);
        return this.fallback();
      }
      this.family = data.family || '';
      this.products = data.products || [];
      this.entries = (data.entries || []).flat();
      this.hex = data.hex || {};
      this.pi = Number(this.dataset.pi || 0);
      this.stateKey = `${this.family || this.products.map((p) => p.id).join('-')}`;

      const currency = this.dataset.currency || 'CAD';
      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        });
      } catch {
        this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency });
      }

      this.grid = this.querySelector('[data-opc-grid]');
      this.nameEl = this.querySelector('[data-opc-name]');
      this.sizesEl = this.querySelector('[data-opc-sizes]');

      // Index colours, merging the same colour across sibling listings
      this.colours = new Map();
      this.sizeOrder = [];
      for (const e of this.entries) {
        e.key = norm(e.c);
        if (!this.colours.has(e.key)) this.colours.set(e.key, { key: e.key, name: e.c, group: e.g, entries: [], el: null });
        this.colours.get(e.key).entries.push(e);
        if (e.s && !this.sizeOrder.includes(e.s)) this.sizeOrder.push(e.s);
      }
      const firstSeen = [...this.sizeOrder];
      this.sizeOrder.sort((a, b) => sizeVal(a) - sizeVal(b) || firstSeen.indexOf(a) - firstSeen.indexOf(b));
      for (const col of this.colours.values()) {
        col.entries.sort((a, b) => this.sizeOrder.indexOf(a.s) - this.sizeOrder.indexOf(b.s));
      }

      this.grid.querySelectorAll('.opc-sw').forEach((el) => {
        const key = norm(el.dataset.c);
        const col = this.colours.get(key);
        if (!col) return el.remove();
        el.dataset.key = key;
        if (col.el) {
          // Same colour on two sibling listings: keep the page product's button
          if (Number(el.dataset.p) === this.pi && Number(col.el.dataset.p) !== this.pi) {
            col.el.replaceWith(el);
            col.el = el;
          } else {
            el.remove();
          }
        } else {
          col.el = el;
        }
      });
      for (const [key, col] of this.colours) if (!col.el) this.colours.delete(key);

      this.current = this.entries.find((e) => String(e.id) === String(this.dataset.variant)) || this.entries[0];
      if (!this.current || this.colours.size < 2) return this.fallback();

      this.sortChromatic();

      this.saved = state[this.stateKey] || {};
      this.buildGroups();
      this.buildSearch();
      this.buildPalette();
      this.bind();
      this.renderSelection();

      // Default view: very large ranges open on the selected colour's group
      const groupCount = this.groupsEl ? this.groupsEl.children.length - 1 : 0;
      let filter = this.saved.group;
      if (filter === undefined) filter = this.colours.size > 40 && groupCount > 1 ? this.current.g : '';
      this.applyFilter(filter, this.saved.query || '', this.saved.expanded || false);
      if (this.saved.palette) this.togglePalette(true);
      this.classList.add('is-ready');
      this.emit();
      this.pruneComplementary();
    }

    // "Pairs well with" often lists the sibling colour listings; the picker already covers them
    pruneComplementary() {
      if (this.products.length < 2) return;
      const info = this.closest('product-info');
      const box = info && info.querySelector('.complementary-products');
      if (!box) return;
      const urls = new Set(this.products.map((p) => (p.u || '').split('?')[0]));
      const prune = () => {
        const cards = box.querySelectorAll('.complementary-slide > *');
        if (!cards.length) return;
        cards.forEach((card) => {
          const a = card.querySelector('a[href*="/products/"]');
          if (!a) return;
          const path = new URL(a.href, location.origin).pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/products\/)/, '');
          if (urls.has(path)) card.remove();
        });
        box.querySelectorAll('.complementary-slide').forEach((sl) => !sl.children.length && sl.remove());
        if (!box.querySelector('.complementary-slide')) box.hidden = true;
      };
      prune();
      new MutationObserver(prune).observe(box, { childList: true, subtree: true });
    }

    fallback() {
      // Show Dawn's picker if ours can't run
      this.hidden = true;
      const dawn = this.parentElement && this.parentElement.querySelector('.opus-dawn-picker');
      if (dawn) dawn.hidden = false;
    }

    save() {
      state[this.stateKey] = {
        group: this.filterGroup,
        query: this.query,
        expanded: this.expanded,
        palette: this.palette,
      };
    }

    /* chromatic order: group (spectrum) → hue → light to dark, from measured swatch colours */

    sortChromatic() {
      const cols = [...this.colours.values()];
      const hasGroups = cols.some((c) => c.group && groupRank(c.group) < GROUP_ORDER.length);
      cols.forEach((c, i) => {
        c.erp = i;
        c.hsl = this.hex[c.key] ? hexToHsl(this.hex[c.key]) : null;
        // measured colour behind the photo, so no white shows at the chip's edges
        const chip = this.hex[c.key] && c.el.querySelector('.opc-sw__chip');
        if (chip) chip.style.backgroundColor = this.hex[c.key];
        c.rank = hasGroups && c.group ? groupRank(c.group) : pseudoGroup(c.hsl);
      });
      if (!cols.some((c) => c.hsl)) return; // nothing measured: keep the ERP order
      cols.sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (!a.hsl || !b.hsl) return (a.hsl ? 0 : 1) - (b.hsl ? 0 : 1) || a.erp - b.erp;
        if (NEUTRAL.has(a.rank)) return b.hsl.l - a.hsl.l;
        return hueKey(a.hsl) - hueKey(b.hsl) || b.hsl.l - a.hsl.l;
      });
      this.colours = new Map(cols.map((c) => [c.key, c]));
      const frag = document.createDocumentFragment();
      cols.forEach((c) => frag.appendChild(c.el));
      this.grid.appendChild(frag);
    }

    /* groups + search */

    buildGroups() {
      this.groupsEl = this.querySelector('[data-opc-groups]');
      const groups = [];
      for (const col of this.colours.values()) {
        if (col.group && !groups.some((g) => norm(g) === norm(col.group))) groups.push(col.group);
      }
      if (groups.length < 2) return;
      groups.sort((a, b) => groupRank(a) - groupRank(b));
      const mk = (label, value, hue, count) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'opc-grp' + (hue ? ' opc-grp--dot' : '');
        b.dataset.group = value;
        b.setAttribute('aria-pressed', 'false');
        b.title = `${label} (${count})`;
        if (hue) {
          b.innerHTML = `<span class="opc-grp__dot" style="background:${hue}"></span><span class="visually-hidden">${label} (${count})</span>`;
        } else {
          b.textContent = label;
        }
        return b;
      };
      this.groupsEl.appendChild(mk('All', '', null, this.colours.size));
      for (const g of groups) {
        const n = [...this.colours.values()].filter((c) => norm(c.group) === norm(g)).length;
        this.groupsEl.appendChild(mk(g, g, groupHue(g), n));
      }
      this.querySelector('[data-opc-tools]').hidden = false;
    }

    buildSearch() {
      if (this.colours.size <= 24) return;
      const wrap = this.querySelector('[data-opc-search-wrap]');
      this.searchEl = this.querySelector('[data-opc-search]');
      this.searchEl.placeholder = `Find a colour (${this.colours.size})`;
      wrap.hidden = false;
      this.querySelector('[data-opc-tools]').hidden = false;
    }

    applyFilter(group, query = '', expanded = false) {
      this.filterGroup = group || '';
      this.query = query || '';
      this.expanded = expanded;
      if (this.searchEl && this.searchEl.value !== this.query) this.searchEl.value = this.query;
      const q = norm(this.query);
      const g = norm(this.filterGroup);
      let shown = 0;
      for (const col of this.colours.values()) {
        const ok = (!g || norm(col.group) === g) && (!q || norm(col.name).includes(q));
        col.el.hidden = !ok;
        if (ok) shown++;
      }
      if (this.groupsEl) {
        for (const b of this.groupsEl.children) {
          b.setAttribute('aria-pressed', String(norm(b.dataset.group) === g));
        }
      }
      this.querySelector('[data-opc-empty]').hidden = shown > 0;
      this.updateCollapse(shown);
      this.save();
    }

    updateCollapse(shown) {
      const more = this.querySelector('[data-opc-more]');
      // about four rows of named swatches
      const cols = Math.max(4, Math.floor(this.grid.clientWidth / 80) || 4);
      const limit = cols * 4;
      const collapse = !this.expanded && !this.query && shown > limit + cols;
      this.grid.classList.toggle('is-collapsed', collapse);
      let i = 0;
      for (const col of this.colours.values()) {
        if (col.el.hidden) continue;
        i++;
        col.el.classList.toggle('is-over', collapse && i > limit && col.key !== this.current.key && !(this.palette && this.picked.has(col.key)));
      }
      more.hidden = !collapse;
      if (collapse) more.textContent = `Show all ${shown} ${this.filterGroup ? this.filterGroup.toLowerCase() + ' ' : ''}colours`;
    }

    /* selection */

    bind() {
      this.grid.addEventListener('click', (ev) => {
        const sw = ev.target.closest('.opc-sw');
        if (!sw) return;
        const col = this.colours.get(sw.dataset.key);
        if (!col) return;
        if (this.palette) return this.toggleTray(col);
        this.chooseColour(col);
      });
      this.groupsEl &&
        this.groupsEl.addEventListener('click', (ev) => {
          const b = ev.target.closest('.opc-grp');
          if (!b) return;
          const same = norm(b.dataset.group) === norm(this.filterGroup);
          this.applyFilter(same ? '' : b.dataset.group, '', false);
        });
      this.searchEl && this.searchEl.addEventListener('input', () => this.applyFilter('', this.searchEl.value, false));
      this.querySelector('[data-opc-more]').addEventListener('click', () =>
        this.applyFilter(this.filterGroup, this.query, true)
      );
      this.querySelector('[data-opc-clear]').addEventListener('click', () => this.applyFilter('', '', false));
      this.sizesEl &&
        this.sizesEl.addEventListener('click', (ev) => {
          const b = ev.target.closest('.opc-size');
          if (!b) return;
          const col = this.colours.get(this.current.key);
          const e = col.entries.find((x) => x.s === b.dataset.s);
          if (e) this.go(e);
        });
    }

    // Best variant of a colour in a size: exact size (available first), else the nearest size
    pickEntry(col, size) {
      const list = col.entries;
      const exact = list.filter((e) => e.s === size);
      const hit = exact.find((e) => e.a && e.p === this.pi) || exact.find((e) => e.a) || exact[0];
      if (hit) return hit;
      const target = sizeVal(size);
      const avail = list.filter((e) => e.a);
      const pool = avail.length ? avail : list;
      if (!Number.isFinite(target)) return pool[0];
      return [...pool].sort((a, b) => Math.abs(sizeVal(a.s) - target) - Math.abs(sizeVal(b.s) - target))[0];
    }

    chooseColour(col) {
      this.go(this.pickEntry(col, this.current.s));
    }

    renderSelection() {
      for (const col of this.colours.values()) col.el.setAttribute('aria-pressed', String(col.key === this.current.key));
      if (this.nameEl) this.nameEl.textContent = this.current.c;
      this.renderSizes();
    }

    renderSizes() {
      if (!this.sizesEl) return;
      const col = this.colours.get(this.current.key);
      const sizes = this.sizeOrder.filter((s) => col.entries.some((e) => e.s === s));
      this.sizesEl.innerHTML = '';
      for (const s of sizes) {
        const e = col.entries.find((x) => x.s === s);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'opc-size' + (e && !e.a ? ' is-soldout' : '');
        b.dataset.s = s;
        b.setAttribute('aria-pressed', String(s === this.current.s));
        b.innerHTML = `<span></span><span class="opc-size__price">${this.fmt(e.pr)}</span>`;
        b.firstChild.textContent = s;
        if (e && !e.a) b.title = 'Sold out online';
        this.sizesEl.appendChild(b);
      }
    }

    fmt(cents) {
      return this.money.format((cents || 0) / 100);
    }

    emit() {
      this.dispatchEvent(
        new CustomEvent('opus:variant', {
          bubbles: true,
          detail: { entry: this.current, label: [this.current.c, this.current.s].filter(Boolean).join(' · ') },
        })
      );
    }

    go(entry) {
      if (!entry || entry.id === this.current.id) return;
      const info = this.closest('product-info');
      this.current = entry;
      this.renderSelection();
      this.updateCollapse([...this.colours.values()].filter((c) => !c.el.hidden).length);
      this.emit();
      this.save();
      if (!info || typeof info.renderProductInfo !== 'function') {
        window.location.href = `${this.products[entry.p].u}?variant=${entry.id}`;
        return;
      }
      info.resetProductFormState && info.resetProductFormState();
      this.classList.add('is-busy');
      this.setAttribute('aria-busy', 'true');

      if (entry.p === this.pi) {
        const url = info.dataset.url;
        info.renderProductInfo({
          requestUrl: `${url}?variant=${entry.id}&section_id=${info.sectionId}`,
          targetId: 'opc-none',
          callback: (html) => {
            info.handleUpdateProductInfo(url)(html);
            this.classList.remove('is-busy');
            this.removeAttribute('aria-busy');
          },
        });
      } else {
        // Colour lives on a sibling listing: swap the buy box and details in place.
        // Reviews, related products etc. stay as rendered (apps like Judge.me only
        // initialise their widgets on page load, so they must not be swapped).
        const url = this.products[entry.p].u;
        const fail = () => (window.location.href = `${url}?variant=${entry.id}`);
        const guard = setTimeout(fail, 9000);
        info.renderProductInfo({
          requestUrl: `${url}?variant=${entry.id}`,
          targetId: 'opc-none',
          callback: (html) => {
            clearTimeout(guard);
            if (!html.querySelector("product-info[id^='MainProduct']")) return fail();
            const nextDetails = html.querySelector('[data-opd]');
            const nextTitle = html.querySelector('head title');
            info.handleSwapProduct(url, false)(html);
            if (nextTitle) document.title = nextTitle.textContent;
            const details = document.querySelector('[data-opd]');
            if (details && nextDetails) {
              details.replaceWith(document.importNode(nextDetails, true));
              initDetails();
            }
          },
        });
      }
    }

    /* palette ("pick several"): every colour keeps its own size and quantity */

    buildPalette() {
      this.toggleBtn = this.querySelector('[data-opc-palette-toggle]');
      this.tray = this.querySelector('[data-opc-tray]');
      if (this.colours.size < 6) return;
      const store = (state.palettes = state.palettes || {});
      // key -> { s: size, q: quantity }
      this.picked = store[this.stateKey] = store[this.stateKey] || new Map();
      this.toggleBtn.hidden = false;
      this.toggleBtn.addEventListener('click', () => this.togglePalette(!this.palette));
      this.querySelector('[data-opc-tray-clear]').addEventListener('click', () => {
        this.picked.clear();
        this.renderTray();
      });
      this.querySelector('[data-opc-tray-add]').addEventListener('click', () => this.addTray());
      this.tray.addEventListener('click', (ev) => {
        // buttons only: the size <select> also carries data-act, and re-rendering on its click
        // destroyed the open dropdown
        const btn = ev.target.closest('button[data-act]');
        if (!btn) return;
        const key = btn.closest('[data-key]').dataset.key;
        const line = this.picked.get(key);
        if (!line) return;
        if (btn.dataset.act === 'rm') this.picked.delete(key);
        if (btn.dataset.act === 'minus') line.q = Math.max(1, line.q - 1);
        if (btn.dataset.act === 'plus') line.q = Math.min(99, line.q + 1);
        this.renderTray();
      });
      this.tray.addEventListener('change', (ev) => {
        const sel = ev.target.closest('select[data-act="size"]');
        if (!sel) return;
        const line = this.picked.get(sel.closest('[data-key]').dataset.key);
        if (line) {
          line.s = sel.value;
          this.lastSize = sel.value;
        }
        this.renderTray();
      });
    }

    togglePalette(on) {
      if (!this.toggleBtn || this.toggleBtn.hidden) return;
      this.palette = on;
      this.classList.toggle('opc--palette', on);
      this.toggleBtn.setAttribute('aria-pressed', String(on));
      this.toggleBtn.textContent = on ? 'Done' : 'Pick several';
      this.tray.hidden = !on;
      const sizes = this.querySelector('[data-opc-sizes-wrap]');
      if (sizes) sizes.hidden = on; // sizes are chosen per colour in the list
      if (on && this.picked.size === 0) this.addPick(this.colours.get(this.current.key), this.current.s);
      this.renderTray();
      this.updateCollapse([...this.colours.values()].filter((c) => !c.el.hidden).length);
      this.save();
    }

    addPick(col, preferred) {
      const e = this.pickEntry(col, preferred || this.lastSize || this.current.s);
      this.picked.set(col.key, { s: e.s, q: 1 });
    }

    toggleTray(col) {
      if (this.picked.has(col.key)) this.picked.delete(col.key);
      else this.addPick(col);
      this.renderTray();
    }

    renderTray() {
      if (!this.tray) return;
      for (const col of this.colours.values()) col.el.classList.toggle('is-picked', !!this.palette && this.picked.has(col.key));
      const list = this.querySelector('[data-opc-tray-list]');
      list.innerHTML = '';
      let total = 0;
      let units = 0;
      let colours = 0;
      for (const [key, line] of this.picked) {
        const col = this.colours.get(key);
        if (!col) continue;
        const e = col.entries.find((x) => x.s === line.s) || this.pickEntry(col, line.s);
        line.s = e.s;
        colours++;
        if (e.a) {
          total += e.pr * line.q;
          units += line.q;
        }
        const li = document.createElement('li');
        li.className = 'opc-line' + (e.a ? '' : ' is-soldout');
        li.dataset.key = key;
        const img = col.el.querySelector('img');
        const sizes = col.entries.length > 1 || e.s;
        li.innerHTML = `
          <span class="opc-line__chip" style="background:${this.hex[key] || ''}">${img ? `<img src="${img.currentSrc || img.src}" alt="" width="32" height="32" loading="lazy">` : ''}</span>
          <span class="opc-line__name"></span>
          ${sizes ? '<select class="opc-line__size" data-act="size" aria-label="Size"></select>' : '<span></span>'}
          <span class="opc-line__qty">
            <button type="button" data-act="minus" aria-label="Fewer">−</button>
            <span class="opc-line__q" aria-live="polite"></span>
            <button type="button" data-act="plus" aria-label="More">+</button>
          </span>
          <span class="opc-line__price"></span>
          <button type="button" class="opc-line__x" data-act="rm" aria-label="Remove">×</button>`;
        li.querySelector('.opc-line__name').textContent = col.name;
        li.querySelector('.opc-line__q').textContent = line.q;
        li.querySelector('.opc-line__price').textContent = e.a ? this.fmt(e.pr * line.q) : 'Sold out';
        const sel = li.querySelector('select');
        if (sel) {
          for (const x of col.entries) {
            const o = document.createElement('option');
            o.value = x.s;
            o.textContent = `${x.s} · ${this.fmt(x.pr)}${x.a ? '' : ' (sold out)'}`;
            o.selected = x.s === e.s;
            sel.appendChild(o);
          }
          if (col.entries.length < 2) sel.disabled = true;
        }
        list.appendChild(li);
      }
      this.querySelector('[data-opc-tray-count]').textContent = `${colours} colour${colours === 1 ? '' : 's'}`;
      this.querySelector('[data-opc-tray-note]').textContent = colours ? 'Tap swatches to add or remove' : '';
      const add = this.querySelector('[data-opc-tray-add]');
      add.disabled = units === 0;
      this.querySelector('[data-opc-tray-label]').textContent =
        units === 0 ? 'Tap colours to add them' : `Add ${units} item${units === 1 ? '' : 's'} · ${this.fmt(total)}`;
      this.save();
    }

    async addTray() {
      const items = [];
      for (const [key, line] of this.picked) {
        const col = this.colours.get(key);
        const e = col && col.entries.find((x) => x.s === line.s);
        if (e && e.a) items.push({ id: e.id, quantity: line.q });
      }
      if (!items.length) return;
      const btn = this.querySelector('[data-opc-tray-add]');
      const msg = this.querySelector('[data-opc-tray-msg]');
      btn.disabled = true;
      btn.classList.add('loading');
      msg.textContent = '';
      try {
        await addItemsToCart(items);
        this.picked.clear();
        this.togglePalette(false);
      } catch (err) {
        msg.textContent = err.message;
      } finally {
        btn.classList.remove('loading');
        btn.disabled = false;
      }
    }
  }
  if (!customElements.get('opus-picker')) customElements.define('opus-picker', OpusPicker);

  /* --------------------------------------------------------------- buy box */

  // Arranges Dawn's buy-box blocks without touching their markup, so apps and Dawn's
  // variant updates keep working. Runs again whenever the buy box is re-rendered (swap).
  class OpusBuybox extends HTMLElement {
    connectedCallback() {
      const info = this.closest('product-info');
      if (!info) return;
      const sid = this.dataset.section;
      const box = info.querySelector(`#ProductInfo-${sid}`);
      if (!box || box.dataset.opusArranged) return;
      box.dataset.opusArranged = '1';
      box.classList.add('opus-buybox');

      // 1. rating + SKU on one quiet line under the title
      const rating = box.querySelector(':scope > .rating-wrapper');
      const sku = box.querySelector(`:scope > #Sku-${sid}`) || box.querySelector(':scope > .product__sku');
      if (rating || sku) {
        const meta = document.createElement('div');
        meta.className = 'opus-meta';
        (rating || sku).before(meta);
        if (rating) {
          const link = document.createElement('a');
          link.className = 'opus-meta__rating';
          link.href = '#opus-reviews';
          link.addEventListener('click', (e) => {
            const target = document.querySelector('.jdgm-widget, #judgeme_product_reviews');
            if (!target) return;
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
          link.appendChild(rating);
          meta.appendChild(link);
        }
        if (sku) meta.appendChild(sku);
      }

      // 2. quantity beside Add to cart
      const qty = box.querySelector(`#Quantity-Form-${sid}`);
      const submit = box.querySelector(`#ProductSubmitButton-${sid}`);
      if (qty && submit) {
        const row = document.createElement('div');
        row.className = 'opus-buyrow';
        submit.before(row);
        row.appendChild(qty);
        row.appendChild(submit);
      }

      // 3. wishlist + share as one quiet row
      const wishBlock = box.querySelector(':scope > .shopify-app-block[id*="wishlist"]');
      const share = box.querySelector(':scope > share-button, :scope > .share-button');
      if (wishBlock || share) {
        const actions = document.createElement('div');
        actions.className = 'opus-actions';
        (wishBlock || share).before(actions);
        if (wishBlock) actions.appendChild(wishBlock);
        if (share) actions.appendChild(share);
      }

      // 4. live cashback amount
      this.cashback = box.querySelector('[data-opus-cb]');
      if (this.cashback) {
        this.money = moneyFormatter(this.cashback.dataset.currency);
        info.addEventListener('opus:variant', (e) => this.updateCashback(e.detail.entry && e.detail.entry.pr));
        info.addEventListener('change', (e) => {
          if (e.target.name !== 'id') return;
          setTimeout(() => {
            try {
              const v = JSON.parse(info.querySelector('variant-selects [data-selected-variant]').textContent);
              this.updateCashback(v && v.price);
            } catch {}
          }, 0);
        });
      }
    }

    updateCashback(cents) {
      if (!this.cashback || typeof cents !== 'number') return;
      this.cashback.querySelectorAll('[data-cb-pct]').forEach((el) => {
        el.textContent = this.money.format(Math.floor(cents * Number(el.dataset.cbPct)) / 10000);
      });
    }
  }
  if (!customElements.get('opus-buybox')) customElements.define('opus-buybox', OpusBuybox);

  function moneyFormatter(currency = 'CAD') {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
    } catch {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency });
    }
  }

  /* ------------------------------------------------------------- sticky buy */

  class OpusStickyBuy extends HTMLElement {
    connectedCallback() {
      const sid = this.dataset.section;
      this.info = this.closest('product-info');
      this.mainBtn = document.getElementById(`ProductSubmitButton-${sid}`);
      if (!this.mainBtn || !this.info) return;
      this.btn = this.querySelector('[data-sticky-add]');
      this.label = this.querySelector('[data-sticky-label]');
      this.variantEl = this.querySelector('[data-sticky-variant]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.mediaEl = this.querySelector('[data-sticky-media]');
      this.sid = sid;

      this.btn.addEventListener('click', () => {
        if (this.mainBtn.disabled || this.mainBtn.getAttribute('aria-disabled') === 'true') {
          this.mainBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        this.mainBtn.click();
      });

      this.sync();
      const picker = this.info.querySelector('opus-picker');
      if (picker && picker.current && this.variantEl) {
        this.variantEl.textContent = [picker.current.c, picker.current.s].filter(Boolean).join(' · ');
      }
      new MutationObserver(() => this.sync()).observe(this.mainBtn, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });
      this.info.addEventListener('opus:variant', (e) => {
        if (this.variantEl) this.variantEl.textContent = e.detail.label;
        const img = e.detail.entry && e.detail.entry.img;
        const el = this.mediaEl && this.mediaEl.querySelector('img');
        if (img && el) {
          el.removeAttribute('srcset');
          el.src = img;
        }
      });
      this.info.addEventListener('change', (e) => {
        if (e.target.name === 'id') setTimeout(() => this.sync(), 0);
      });

      // Show while the real button is off screen (above or below the viewport)
      this.check = () => {
        if (!this.isConnected || !this.mainBtn.isConnected) return;
        const r = this.mainBtn.getBoundingClientRect();
        if (!r.width && !r.height) return this.toggle(false);
        // Phones: whenever the button is off screen. Desktop: only once scrolled past it.
        const phone = window.innerWidth < 990;
        this.toggle(r.bottom < 0 || (phone && r.top > window.innerHeight));
      };
      this.io = new IntersectionObserver(() => this.check(), { threshold: 0 });
      this.io.observe(this.mainBtn);
      // Scroll fallback (IntersectionObserver is paused in background tabs / some webviews)
      let t = 0;
      this.onScroll = () => {
        if (t) return;
        t = setTimeout(() => {
          t = 0;
          this.check();
        }, 120);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      setTimeout(this.check, 300);
    }

    disconnectedCallback() {
      this.io && this.io.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.body.classList.remove('opus-sticky-on');
    }

    toggle(on) {
      this.hidden = !on;
      document.body.classList.toggle('opus-sticky-on', on);
    }

    sync() {
      const text = this.mainBtn.querySelector('span:not(.loading__spinner)');
      this.label.textContent = (text ? text.textContent : this.mainBtn.textContent).trim();
      const off = this.mainBtn.disabled || this.mainBtn.getAttribute('aria-disabled') === 'true';
      this.btn.classList.toggle('is-disabled', off);
      if (this.variantEl && !this.info.querySelector('opus-picker.is-ready')) {
        try {
          const v = JSON.parse(this.info.querySelector('variant-selects [data-selected-variant]').textContent);
          if (v && v.options) this.variantEl.textContent = v.options.join(' · ');
        } catch {}
      }
      const priceBox = document.getElementById(`price-${this.sid}`);
      if (priceBox && this.priceEl) {
        const onSale = priceBox.querySelector('.price--on-sale');
        const el = priceBox.querySelector(onSale ? '.price-item--sale' : '.price__regular .price-item--regular');
        if (el && el.textContent.trim()) this.priceEl.textContent = el.textContent.trim().replace(/\s+/g, ' ');
      }
    }
  }
  if (!customElements.get('opus-sticky-buy')) customElements.define('opus-sticky-buy', OpusStickyBuy);

  /* ---------------------------------------------------------- details section */

  function initDetails(root = document) {
    root.querySelectorAll('[data-opd]').forEach((sec) => {
      if (sec._ready) return;
      sec._ready = true;
      const desc = sec.querySelector('[data-opd-desc]');
      const more = sec.querySelector('[data-opd-more]');
      if (desc && more) {
        setTimeout(() => {
          if (desc.scrollHeight > desc.clientHeight + 24) {
            more.hidden = false;
          } else {
            desc.classList.remove('is-clamped');
          }
        }, 60);
        more.addEventListener('click', () => {
          const open = desc.classList.toggle('is-clamped') === false;
          more.setAttribute('aria-expanded', String(open));
          more.textContent = open ? more.dataset.less : more.dataset.more;
        });
      }
      let variants = {};
      try {
        variants = JSON.parse(sec.querySelector('[data-opd-variants]').textContent);
      } catch {}
      const apply = (id) => {
        const v = variants[id];
        if (!v) return;
        sec.querySelectorAll('[data-opd-field]').forEach((cell) => {
          const val = v[cell.dataset.opdField];
          const row = cell.closest('[data-opd-row]');
          cell.textContent = val || '';
          if (row) row.hidden = !val;
        });
      };
      document.addEventListener('change', (e) => {
        if (e.target.name === 'id' && e.target.closest('product-form, form[id^="product-form-template"]')) apply(e.target.value);
      });
    });
  }

  /* ------------------------------------------------------------ learn section */

  function initLearn(root = document) {
    root.querySelectorAll('[data-opl-articles]').forEach((box) => {
      if (box._ready) return;
      box._ready = true;
      const q = box.dataset.query;
      const limit = Number(box.dataset.limit || 3);
      const have = new Set((box.dataset.exclude || '').split(',').filter(Boolean));
      const run = async () => {
        const list = box.querySelector('[data-opl-list]');
        const already = list.children.length;
        if (!q || already >= limit) return finish(box);
        try {
          const url = `${(window.routes && window.routes.predictive_search_url) || '/search/suggest'}.json?q=${encodeURIComponent(q)}&resources[type]=article&resources[limit]=${limit + have.size}&resources[options][fields]=title,tag,body`;
          const res = await fetch(url);
          const json = await res.json();
          const arts = ((json.resources && json.resources.results && json.resources.results.articles) || []).filter(
            (a) => !have.has(a.handle)
          );
          for (const a of arts.slice(0, limit - already)) {
            const li = document.createElement('li');
            li.className = 'opl-card';
            const href = a.url.split('?')[0];
            const img = a.featured_image && a.featured_image.url ? a.featured_image.url : a.image;
            const video = (a.tags || []).some((t) => /video|podcast/i.test(t));
            li.innerHTML = `<a class="opl-card__link" href="${href}">
              <span class="opl-card__media">${img ? `<img src="${img}${img.includes('?') ? '&' : '?'}width=480" alt="" loading="lazy" width="480" height="300">` : ''}</span>
              <span class="opl-card__kicker">${video ? 'Watch' : 'Read'}</span>
              <span class="opl-card__title"></span></a>`;
            li.querySelector('.opl-card__title').textContent = a.title;
            list.appendChild(li);
          }
        } catch (e) {
          console.warn('opus learn: articles unavailable', e);
        }
        finish(box);
      };
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        io.disconnect();
        run();
      };
      const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && start(), {
        rootMargin: '600px 0px',
      });
      io.observe(box);
      // Fallback for paused observers: load after the page settles anyway
      setTimeout(start, 5000);
    });
  }

  function finish(box) {
    const list = box.querySelector('[data-opl-list]');
    box.hidden = !list || list.children.length === 0;
    const sec = box.closest('[data-opl]');
    if (sec) {
      const anyVisible = [...sec.querySelectorAll('[data-opl-col]')].some((c) => !c.hidden);
      sec.hidden = !anyVisible;
    }
  }

  const boot = () => {
    initDetails();
    initLearn();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Dawn's product swap inserts a new <main> beside the old one: init the new sections
  const mainEl = document.querySelector('main');
  if (mainEl && mainEl.parentNode) {
    new MutationObserver((muts) => {
      if (muts.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1))) boot();
    }).observe(mainEl.parentNode, { childList: true });
  }
})();
